import { is } from 'date-fns/locale';
import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Updated Brand interface based on actual API response
export interface Brand {
  _id: string;
  name: string;
  email: string;
  mobileNo: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  groupId?: string;
  token?: string;
  category?: string[];
  subCategory?: string[];
  schemeBucket?: string[];
  dashboard?: boolean;
  suspended?: boolean;
  isEv?: boolean;
  isBrand?: boolean;
  isPublic?: boolean;
  publicFor?: string[];
  lender?: string;
  kycGstNo?: string;
  kycBank?: {
    accountNo?: string;
    holderName?: string;
    ifsc?: string;
  };
  kycShop?: {
    name?: string;
    address?: string;
    state?: string;
    city?: string;
    pin?: string;
    note?: string;
  };
  kycDocs?: {
    shopFront?: string;
    shopInner?: string;
    evCer?: string;
  };
  kyc?: {
    status?: string;
    bank?: boolean;
    shop?: boolean;
    docs?: boolean;
  };
  serialNumber?: number;
}

// API response format
interface BrandApiResponse {
  status: string;
  result: {
    data: {
      totalMatch: number;
      pageNo: number;
      pageSize: number;
      results: Brand[];
    };
  };
}

/**
 * Fetch Brands with pagination, sorting and filtering
 * @param token Authentication token
 * @param page Current page number
 * @param pageSize Number of items per page
 * @param searchQuery Search query for filtering
 * @param sortBy Field to sort by
 * @param sortOrder Sort direction (asc or desc)
 * @returns Promise with Brands data and pagination info
 */
export const fetchBrands = async ({
  page = 1,
  pageSize = 10,
  sortField = "_id",
  sortOrder = "desc",
  search = "",
  token = "",
}: {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  search?: string;
  token?: string;
}): Promise<{
  Brands: Brand[];
  totalMatch: number;
  pageNo: number;
  pageSize: number;
  clientPaginated?: boolean;
  paginationMessage?: string;
}> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Map client-side sort fields to API sort fields
    const fieldMap: Record<string, string> = {
      name: "name",
      email: "email",
      contactNo: "mobileNo",
      mobileNo: "mobileNo",
      groupId: "groupId",
      status: "status",
      createdAt: "createdAt",
      _id: "_id",
    };

    const apiSortField = fieldMap[sortField] || "createdAt";

    // Properly format query params
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    params.append("sortBy", apiSortField);
    params.append("sortOrder", sortOrder);
    if (search) params.append("searchQuery", search);

    const url = `/adminusers/v1/brand?${params.toString()}`;

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    const response = await axios.get<BrandApiResponse>(url, {
      headers: {
        'Content-Type': 'application/json',
        'token': authToken
      }
    });

    if (!response.data || typeof response.data !== 'object') {
      throw new Error("Invalid API response format");
    }

    let Brands: Brand[] = [];
    let totalMatch = 0;
    let pageNo = page;
    let actualPageSize = pageSize;
    let usingClientPagination = false;
    let paginationMessage = "Server-side pagination is active";

    // Check if the response follows the expected format
    if (response.data.status === "Request successful!" && response.data.result?.data?.results) {
      Brands = response.data.result.data.results;
      totalMatch = response.data.result.data.totalMatch || Brands.length;

      // Get actual page and pageSize from response if available
      if (response.data.result?.data?.pageNo !== undefined) {
        pageNo = response.data.result.data.pageNo;
      }

      if (response.data.result?.data?.pageSize !== undefined) {
        actualPageSize = response.data.result.data.pageSize;
      }

      // We trust the server to handle pagination correctly now that we have totalMatch
      // No need to implement client-side pagination as a fallback
      console.log(`Server pagination: Page ${pageNo} of ${Math.ceil(totalMatch / actualPageSize)}, showing ${Brands.length} of ${totalMatch} total Brands`);

      // Add serial numbers to Brands based on their position in the paginated results
      Brands = Brands.map((Brand, index) => {
        const serialNumber = (pageNo - 1) * actualPageSize + index + 1;
        return {
          ...Brand,
          serialNumber: serialNumber
        };
      });
    } else {
      throw new Error("Unexpected API response structure");
    }

    return {
      Brands,
      totalMatch,
      pageNo,
      pageSize: actualPageSize,
      clientPaginated: usingClientPagination,
      paginationMessage
    };
  } catch (error: any) {
    console.error("Error fetching Brands:", error.message);
    throw error;
  }
};

/**
 * Fetch a single Brand by ID
 * @param id Brand ID
 * @param token Authentication token
 * @returns Promise with Brand data
 */
export const fetchBrandById = async (
  id: string,
  token: string | null
): Promise<Brand> => {
  try {
    console.log(`[API] Fetching Brand with ID: ${id}`);

    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
      console.log("[API] Token from props was empty, using token from localStorage:", authToken ? "Found token" : "No token in localStorage");
    }

    const response = await axios.get(`/adminusers/v1/brand/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': authToken
      }
    });

    console.log('[API] Brand detail response:', response.data);

    if (response.data?.status === "Request successful!" && response.data.result?.data?.results?.[0]) {
      return response.data.result.data.results[0];
    }

    throw new Error('Invalid response format from API');
  } catch (error: any) {
    console.error('[API] Error fetching Brand:');
    const errorMessage = error.response?.data?.message || 'Failed to fetch Brand details';
    showToast.error(errorMessage);

    throw error;
  }
};

// Update interfaces for create and update
type CreateBrandData = {
  name: string;
  groupId: string;
  mobileNo: string;
  email: string;
  isEv?: boolean;
  isBrand?: boolean;
  isPublic?: boolean;
  publicFor: string[];
  category?: string[];
  subCategory?: string[];
  schemeBucket?: string[];
  dashboard?: boolean;
  suspended?: boolean;
  lender?: string;
  kycGstNo?: string;
  kyc?: {
    bank?: {
      accountNo?: string;
      holderName?: string;
      ifsc?: string;
    };
    shop?: {
      name?: string;
      address?: string;
      state?: string;
      city?: string;
      pin?: string;
    };
    docs?: {
      shopFront?: File | null;
      shopInner?: File | null;
      evCer?: File | null;
    };
  };
};

/**
 * Create a new Brand
 * @param BrandData Brand data to create
 * @param token Authentication token
 * @param docFiles Optional document files for KYC
 * @returns Promise with the created Brand
 */
export const createBrand = async (
  BrandData: CreateBrandData,
  token: string | null,
  docFiles?: {
    shopFront?: File | null;
    shopInner?: File | null;
    evCer?: File | null;
  }
): Promise<Brand> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    console.log("[API Debug] Creating Brand with data:", JSON.stringify(BrandData, null, 2));
    console.log("[API Debug] Token available:", !!authToken);

    // Convert form data to the structure expected by the API
    const apiBrandData: any = {
      name: BrandData.name,
      groupId: BrandData.groupId || BrandData.name, // Ensure groupId is present
      mobileNo: BrandData.mobileNo,
      email: BrandData.email,
      isEv: BrandData.isEv || false,
      isBrand: BrandData.isBrand || false,
      isPublic: BrandData.isPublic || false,
      publicFor: BrandData.publicFor,
      category: BrandData.category || [],
      subCategory: BrandData.subCategory || [],
      schemeBucket: BrandData.schemeBucket || [],
      dashboard: BrandData.dashboard || false,
      suspended: BrandData.suspended || false
    };

    // Add lender if provided
    if (BrandData.lender) {
      apiBrandData.lender = BrandData.lender;
    }

    if (BrandData.kycGstNo) {
      apiBrandData.kycGstNo = BrandData.kycGstNo;
    }

    // Initialize KYC flags
    const kycFlags = {
      bank: false,
      shop: false,
      docs: false,
      status: 'false' as 'true' | 'false'
    };

    // Handle kycBank if present
    if (BrandData.kyc?.bank) {
      apiBrandData.kycBank = {
        accountNo: BrandData.kyc.bank.accountNo,
        holderName: BrandData.kyc.bank.holderName,
        ifsc: BrandData.kyc.bank.ifsc
      };
      kycFlags.bank = true;
    }

    // Handle kycShop if present
    if (BrandData.kyc?.shop) {
      apiBrandData.kycShop = {
        name: BrandData.kyc.shop.name,
        address: BrandData.kyc.shop.address,
        state: BrandData.kyc.shop.state,
        city: BrandData.kyc.shop.city,
        pin: BrandData.kyc.shop.pin
      };
      kycFlags.shop = true;
    }

    // Handle documents if present
    if (docFiles && (docFiles.shopFront || docFiles.shopInner || docFiles.evCer)) {
      kycFlags.docs = true;
    }

    // NOTE: No longer automatically setting KYC status
    // KYC status will need to be set manually via the admin interface
    kycFlags.status = 'false';

    // Add KYC flags to the API data
    apiBrandData.kyc = kycFlags;

    console.log("[API Debug] Transformed API data:", JSON.stringify(apiBrandData, null, 2));
    console.log("[API Debug] Document files present:", !!docFiles);

    // Step 1: Create the Brand
    try {
      const response = await axios.post('/adminusers/v1/partner', apiBrandData, {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      });

      console.log("[API Debug] Brand creation response:", response.data);

      let createdBrand: Brand;

      // Handle successful response with different formats
      if (response.data?.status === "Request successful!" && response.data.result?.data?.results?.[0]) {
        // Standard Brand response format
        createdBrand = response.data.result.data.results[0];
      } else if (response.data?.status === "Created!" && response.data.result?.user) {
        // Alternative format when creating a Brand
        // Map the user object to our Brand interface
        createdBrand = {
          _id: response.data.result.user._id,
          name: response.data.result.user.name,
          email: response.data.result.user.email,
          mobileNo: response.data.result.user.mobileNo,
          status: response.data.result.user.suspended ? 'suspended' : 'active',
          createdAt: response.data.result.user.createdAt,
          updatedAt: response.data.result.user.updatedAt,
          groupId: response.data.result.user.groupId,
          token: response.data.result.user.token,
          isEv: response.data.result.user.isEv || false,
          isPublic: response.data.result.user.isPublic || false,
          publicFor: response.data.result.user.publicFor || [],
          isBrand: true,
          lender: response.data.result.user.lender || '',
          kycGstNo: response.data.result.user.kycGstNo,
          kycBank: response.data.result.user.kycBank,
          kycShop: response.data.result.user.kycShop,
          kycDocs: response.data.result.user.kycDocs,
          kyc: response.data.result.user.kyc || {
            status: '',
            bank: false,
            shop: false,
            docs: false
          }
        };
      } else {
        throw new Error('Invalid response format from API');
      }

      // Step 2: Upload documents if provided
      const mobileNo = createdBrand.mobileNo;

      if (docFiles && mobileNo) {
        const uploadPromises: Promise<any>[] = [];

        // Upload Shop Front image if exists
        if (docFiles.shopFront) {
          uploadPromises.push(uploadBrandDocument('Brand', 'shopFront', mobileNo, docFiles.shopFront, authToken));
        }

        // Upload Shop Inner image if exists
        if (docFiles.shopInner) {
          uploadPromises.push(uploadBrandDocument('Brand', 'shopInner', mobileNo, docFiles.shopInner, authToken));
        }

        // Upload EV Certificate if exists and Brand is EV
        if (docFiles.evCer && BrandData.isEv) {
          uploadPromises.push(uploadBrandDocument('Brand', 'evCer', mobileNo, docFiles.evCer, authToken));
        }

        // Wait for all uploads to complete
        if (uploadPromises.length > 0) {
          try {
            console.log("[API Debug] Uploading", uploadPromises.length, "document files");
            await Promise.all(uploadPromises);
            console.log('[API] Successfully uploaded all Brand documents');
          } catch (uploadError) {
            console.error('[API] Error uploading some Brand documents:', uploadError);
            // We don't throw here as the Brand is already created
          }
        }
      }

      return createdBrand;
    } catch (error: any) {
      console.error("[API Debug] Detailed error:", error.response?.status, error.response?.data);
      console.error("[API Debug] Request URL:", error.config?.url);
      console.error("[API Debug] Request Headers:", error.config?.headers);
      throw error;
    }
  } catch (error: any) {
    console.error('[API] Error creating Brand:', error.message);
    const errorMessage = error.response?.data?.message || 'Failed to create Brand';
    showToast.error(errorMessage);

    throw error;
  }
};

/**
 * Upload document for Brand KYC
 * @param type User type (hardcoded as 'Brand')
 * @param docType Document type (shopFront, shopInner, evCer)
 * @param mobileNo Mobile number of the Brand
 * @param file File to upload
 * @param token Authentication token
 * @returns Promise indicating success
 */
export const uploadBrandDocument = async (
  type: string,
  docType: 'shopFront' | 'shopInner' | 'evCer',
  mobileNo: string,
  file: File,
  token: string
): Promise<boolean> => {
  try {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      console.error(`[API] Invalid file type for ${docType}. Only PNG, JPG, and JPEG are allowed.`);
      showToast.error(`Invalid file type. Only PNG, JPG, and JPEG are allowed.`);
      return false;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      console.error(`[API] File too large for ${docType}. Maximum size is 5MB.`);
      showToast.error(`File too large. Maximum size is 5MB.`);
      return false;
    }

    // Create form data
    const formData = new FormData();
    formData.append('file', file);

    // Use 'Brand' as the user type in the endpoint
    const endpoint = `/adminusers/partner/docs/${docType}/${mobileNo}`;

    console.log(`[API Debug] Document upload details:`);
    console.log(`[API Debug] - DocType: ${docType}`);
    console.log(`[API Debug] - Mobile: ${mobileNo}`);
    console.log(`[API Debug] - Endpoint: ${endpoint}`);
    console.log(`[API Debug] - File name: ${file.name}`);
    console.log(`[API Debug] - File size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);

    try {
      // First attempt with multipart/form-data (standard for file uploads)
      console.log(`[API Debug] Attempting upload with multipart/form-data...`);
      const response = await axios.put(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'token': token
        }
      });

      console.log(`[API Debug] Upload success with status: ${response.status}`);
      return true;
    } catch (formDataError: any) {
      console.error(`[API Debug] Form data upload failed:`, formDataError.message);

      if (formDataError.response?.data?.status === 'Bad request, malformed JSON') {
        // The server might be expecting a different format
        console.log(`[API Debug] Server reported malformed JSON, trying alternative format...`);

        try {
          // Read the file as base64
          const base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
          });

          // Format: data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/...
          // Extract only the base64 part after the comma
          const base64Content = base64Data.split(',')[1];

          // Create JSON payload
          const jsonPayload = {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            fileContent: base64Content
          };

          // Try with JSON format
          console.log(`[API Debug] Trying JSON format with base64 encoded file...`);
          const jsonResponse = await axios.put(endpoint, jsonPayload, {
            headers: {
              'Content-Type': 'application/json',
              'token': token
            }
          });

          console.log(`[API Debug] JSON upload success with status: ${jsonResponse.status}`);
          return true;
        } catch (jsonError: any) {
          console.error(`[API Debug] JSON upload also failed:`, jsonError.message);

          try {
            // Try with URL encoded form
            const urlEncodedForm = new URLSearchParams();
            // We can't include the file directly in URL encoded form, but we can add metadata
            urlEncodedForm.append('filename', file.name);
            urlEncodedForm.append('filetype', file.type);
            urlEncodedForm.append('filesize', file.size.toString());

            console.log(`[API Debug] Trying with x-www-form-urlencoded (metadata only)...`);
            const urlEncodedResponse = await axios.put(endpoint, urlEncodedForm, {
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'token': token
              }
            });

            console.log(`[API Debug] URL encoded upload success with status: ${urlEncodedResponse.status}`);
            return true;
          } catch (urlEncodedError: any) {
            console.error(`[API Debug] URL encoded upload also failed:`, urlEncodedError.message);
            // If all attempts fail, throw the original error
            throw formDataError;
          }
        }
      } else {
        // If it's not a malformed JSON error, just throw the original error
        throw formDataError;
      }
    }
  } catch (error: any) {
    console.error(`[API] Error uploading ${docType} document:`, error.message);
    if (error.response) {
      console.error(`[API] Response status:`, error.response.status);
      console.error(`[API] Response data:`, error.response.data);
    }

    const errorMessage = error.response?.data?.message ||
      error.response?.data?.result ||
      `Failed to upload ${docType} document`;
    showToast.error(errorMessage);
    throw error;
  }
};

/**
 * Update an existing Brand
 * @param idOrNameOrMobile Brand ID, name, or mobile number to update
 * @param BrandData Brand data to update
 * @param token Authentication token
 * @param docFiles Optional document files for KYC
 * @returns Promise with the updated Brand
 */
export const updateBrand = async (
  idOrNameOrMobile: string,
  BrandData: Partial<{
    name: string;
    groupId: string;
    mobileNo: string;
    email: string;
    suspended: boolean;
    category: string[];
    subCategory: string[];
    schemeBucket: string[];
    dashboard: boolean;
    isBrand: boolean;
    isEv: boolean;
    isPublic: boolean;
    publicFor: string[];
    lender: string;
    kycGstNo?: string;
    kycBank?: {
      accountNo?: string;
      holderName?: string;
      ifsc?: string;
    };
    kycShop?: {
      name?: string;
      address?: string;
      state?: string;
      city?: string;
      pin?: string;
      note?: string;
    };
    kyc?: {
      status?: string;
      bank?: boolean;
      shop?: boolean;
      docs?: boolean;
    };
    activated?: boolean;
    verified?: boolean;
  }>,
  token: string | null,
  docFiles?: {
    shopFront?: File | null;
    shopInner?: File | null;
    evCer?: File | null;
  }
): Promise<Brand> => {
  try {
    let authToken = token || localStorage.getItem('token') || '';
    if (!authToken) throw new Error("No authentication token available");

    if (!BrandData.kyc) {
      if (BrandData.kycBank || BrandData.kycShop || (docFiles && (docFiles.shopFront || docFiles.shopInner || docFiles.evCer))) {
        const kycFlags: { bank?: boolean; shop?: boolean; docs?: boolean } = {};
        if (BrandData.kycBank) kycFlags.bank = true;
        if (BrandData.kycShop) kycFlags.shop = true;
        if (docFiles && (docFiles.shopFront || docFiles.shopInner || docFiles.evCer)) kycFlags.docs = true;
        if (Object.keys(kycFlags).length > 0) BrandData.kyc = kycFlags;
      }
    }

    console.log(`[Update Debug] Starting Brand update with identifier: ${idOrNameOrMobile}`);
    const adaptedData = adaptBrandData(BrandData);

    if (adaptedData.mobileNo && !/^\d{10}$/.test(adaptedData.mobileNo)) throw new Error("Mobile number must be 10 digits");

    let endpoint = '/adminusers/v1/partner';
    const queryParams = new URLSearchParams();
    const identifierType = getIdentifierType(idOrNameOrMobile);
    if (identifierType === 'id') queryParams.append('id', idOrNameOrMobile);
    else if (identifierType === 'mobile') queryParams.append('mobileNo', idOrNameOrMobile);
    else if (identifierType === 'name') queryParams.append('name', idOrNameOrMobile.trim());
    endpoint += `?${queryParams.toString()}`;

    const updateData: Record<string, any> = {};
    if (adaptedData.name) updateData.name = adaptedData.name.trim();
    if (adaptedData.email !== undefined) updateData.email = adaptedData.email !== null ? adaptedData.email.trim() : null;
    if (adaptedData.mobileNo !== undefined) updateData.mobileNo = adaptedData.mobileNo !== null ? adaptedData.mobileNo.trim() : null;
    if (adaptedData.category) updateData.category = adaptedData.category;
    if (adaptedData.subCategory) updateData.subCategory = adaptedData.subCategory;
    if (adaptedData.schemeBucket) updateData.schemeBucket = adaptedData.schemeBucket;
    if (adaptedData.dashboard !== undefined) updateData.dashboard = adaptedData.dashboard;
    if (adaptedData.isBrand !== undefined) updateData.isBrand = adaptedData.isBrand;
    if (adaptedData.suspended !== undefined) updateData.suspended = adaptedData.suspended;
    if (adaptedData.groupId) updateData.groupId = adaptedData.groupId.trim();
    else if (adaptedData.name) updateData.groupId = adaptedData.name.trim();
    if (adaptedData.isEv !== undefined) updateData.isEv = adaptedData.isEv;
    if (adaptedData.isPublic !== undefined) updateData.isPublic = adaptedData.isPublic;
    if (adaptedData.publicFor !== undefined) updateData.publicFor = adaptedData.publicFor;
    if (adaptedData.lender !== undefined) updateData.lender = adaptedData.lender;
    if (adaptedData.kycGstNo !== undefined) updateData.kycGstNo = adaptedData.kycGstNo;
    if (adaptedData.activated !== undefined) updateData.activated = adaptedData.activated;
    if (adaptedData.verified !== undefined) updateData.verified = adaptedData.verified;
    if (adaptedData.kyc) updateData.kyc = adaptedData.kyc;
    if (adaptedData.kycBank) updateData.kycBank = adaptedData.kycBank;
    if (adaptedData.kycShop) updateData.kycShop = adaptedData.kycShop;

    if (Object.keys(updateData).length === 0) throw new Error("No valid data provided for update");

    let updatedBrand: Brand;

    try {
      const response = await axios.put(endpoint, updateData, {
        headers: { 'Content-Type': 'application/json', 'token': authToken }
      });

      if (response.data?.status === "Request successful!" || response.data?.status === "Updated!" || response.status === 200) {
        if (response.data.result?.user) updatedBrand = mapUserToBrand(response.data.result.user);
        else if (response.data.result?.data?.results?.[0]) updatedBrand = response.data.result.data.results[0];
        else throw new Error('Invalid response format from API');
      } else {
        throw new Error('Invalid response format from API');
      }

    } catch (jsonError: any) {
      const data = jsonError.response?.data;

      // Early exit: Mobile number already exists → do NOT fallback
      if (data?.status === "Bad request, malformed JSON" && data?.result === "Mobile number already exists") {
        showToast.error(data.result);
        throw jsonError;
      }

      // Only fallback for other 400 malformed JSON errors
      if (jsonError.response?.status === 400 &&
        (data?.status === "Bad request, malformed JSON" || data?.result === "none")) {

        const formData = new URLSearchParams();
        Object.entries(updateData).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) formData.append(key, JSON.stringify(value));
          else formData.append(key, String(value));
        });

        try {
          const formResponse = await axios.put(endpoint, formData.toString(), {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'token': authToken }
          });

          if (formResponse.data?.status === "Request successful!" ||
            formResponse.data?.status === "Updated!" || formResponse.status === 200) {

            if (formResponse.data.result?.user) updatedBrand = mapUserToBrand(formResponse.data.result.user);
            else if (formResponse.data.result?.data?.results?.[0]) updatedBrand = formResponse.data.result.data.results[0];
            else throw new Error('Invalid response format from API (form data)');
          } else throw new Error('Invalid response format from API (form data)');
        } catch (formError: any) {
          throw formError;
        }
      } else throw jsonError;
    }

    if (docFiles && adaptedData.mobileNo) {
      const mobileNo = adaptedData.mobileNo;
      const uploadPromises: Promise<any>[] = [];
      if (docFiles.shopFront) uploadPromises.push(uploadBrandDocument('Brand', 'shopFront', mobileNo, docFiles.shopFront, authToken));
      if (docFiles.shopInner) uploadPromises.push(uploadBrandDocument('Brand', 'shopInner', mobileNo, docFiles.shopInner, authToken));
      if (docFiles.evCer && (adaptedData.isEv || updatedBrand.isEv)) uploadPromises.push(uploadBrandDocument('Brand', 'evCer', mobileNo, docFiles.evCer, authToken));
      if (uploadPromises.length > 0) await Promise.all(uploadPromises);
    }

    console.log('[Update Debug] Successfully updated Brand:', JSON.stringify(updatedBrand, null, 2));
    return updatedBrand!;
  } catch (error: any) {
    console.error('[API] Error updating Brand:', error.message);

    if (error.response) {
      const data = error.response.data;
      if (data?.status === "Bad request, malformed JSON" && data?.result === "Mobile number already exists") {
        showToast.error(data.result);
      } else showToast.error('Failed to update');
    } else showToast.error('Failed to update: Network error');

    throw error;
  }
};


/**
 * Adapter function to handle different input formats for Brand data
 * The UI component might use contactNo/contactEmail while the API expects mobileNo/email
 */
function adaptBrandData(data: any): Partial<{
  name: string;
  groupId: string;
  mobileNo: string;
  email: string;
  suspended: boolean;
  isEv: boolean;
  isBrand: boolean;
  isPublic: boolean;
  publicFor: string[];
  category: string[];
  subCategory: string[];
  schemeBucket: string[];
  dashboard: boolean;
  lender: string;
  kycGstNo?: string;
  kycBank?: {
    accountNo?: string;
    holderName?: string;
    ifsc?: string;
  };
  kycShop?: {
    name?: string;
    address?: string;
    state?: string;
    city?: string;
    pin?: string;
    note?: string;
  };
  kyc?: {
    status?: string;
    bank?: boolean;
    shop?: boolean;
    docs?: boolean;
  };
  activated?: boolean;
  verified?: boolean;
}> {
  // Create a new object to avoid modifying the original
  const adaptedData: any = { ...data };

  // Handle contactNo (from UI) → mobileNo (for API)
  if (adaptedData.contactNo !== undefined && adaptedData.mobileNo === undefined) {
    adaptedData.mobileNo = adaptedData.contactNo;
    delete adaptedData.contactNo;
  }

  // Handle contactEmail (from UI) → email (for API)
  if (adaptedData.contactEmail !== undefined && adaptedData.email === undefined) {
    adaptedData.email = adaptedData.contactEmail;
    delete adaptedData.contactEmail;
  }

  // Ensure groupId matches name if not provided
  if (adaptedData.name && !adaptedData.groupId) {
    adaptedData.groupId = adaptedData.name;
  }

  return adaptedData;
}

// Helper function to determine identifier type
function getIdentifierType(identifier: string): 'id' | 'mobile' | 'name' {
  // If it looks like a MongoDB ID (24 hex characters)
  if (/^[0-9a-fA-F]{24}$/.test(identifier)) {
    return 'id';
  }

  // If it looks like a mobile number (10 digits)
  if (/^\d{10}$/.test(identifier)) {
    return 'mobile';
  }

  // Otherwise, assume it's a name
  return 'name';
}

// Helper function to handle successful response
function handleSuccessResponse(response: any): Brand {
  if (response.data?.status === "Request successful!" || response.data?.status === "Updated!") {
    return response.data.result?.data?.results?.[0] ? response.data.result.data.results[0] : mapUserToBrand(response.data.result.user);
  }

  throw new Error('Invalid response format from API');
}

// Helper function to map user object to Brand interface
function mapUserToBrand(user: any): Brand {
  return {
    _id: user._id,
    name: user.name,
    email: user.email,
    mobileNo: user.mobileNo,
    status: user.suspended ? 'suspended' : 'active',
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    groupId: user.groupId,
    token: user.token,
    isEv: user.isEv,
    isPublic: user.isPublic,
    publicFor: user.publicFor,
    isBrand: true,
    lender: user.lender,
    kycGstNo: user.kycGstNo,
    kycBank: user.kycBank,
    kycShop: user.kycShop,
    kycDocs: user.kycDocs,
    kyc: user.kyc || {
      status: '',
      bank: false,
      shop: false,
      docs: false
    }
  };
}

/**
 * Delete a Brand
 * @param nameOrMobile Brand name, or mobile number to delete
 * @param token Authentication token
 * @returns Promise indicating success
 */
export const deleteBrand = async (
  nameOrMobile: string,
  token: string | null
): Promise<boolean> => {
  try {
    // Get token from localStorage if not provided
    let authToken = token;
    if (!authToken) {
      authToken = localStorage.getItem('token') || '';
    }

    // Check if token exists
    if (!authToken) {
      throw new Error("No authentication token available");
    }

    console.log(`[Delete] Starting Brand deletion with identifier: ${nameOrMobile}`);

    // Try name first
    try {
      const nameEndpoint = `/adminusers/v1/partner?name=${encodeURIComponent(nameOrMobile)}`;
      console.log(`[Delete] Trying with name query param: ${nameEndpoint}`);

      const nameResponse = await axios.delete(nameEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      });

      return handleDeleteSuccess(nameResponse);
    } catch (nameError: any) {
      console.error('[Delete] Name approach failed:', nameError.message);
      // Continue to next method
    }

    // Try mobile number second
    try {
      const mobileEndpoint = `/adminusers/v1/partner?mobileNo=${nameOrMobile}`;
      console.log(`[Delete] Trying with mobileNo query param: ${mobileEndpoint}`);

      const mobileResponse = await axios.delete(mobileEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': authToken
        }
      });

      return handleDeleteSuccess(mobileResponse);
    } catch (mobileError: any) {
      console.error('[Delete] mobileNo approach failed:', mobileError.message);
      throw mobileError;
    }
  } catch (error: any) {
    console.error('[API] Error deleting Brand:', error.message);

    // More specific error based on status code
    let errorMessage = 'Failed to delete Brand';
    if (error.response) {
      if (error.response.status === 404) {
        errorMessage = 'Brand not found. Please check the name or mobile number.';
      } else if (error.response.status === 403) {
        errorMessage = 'You do not have permission to delete this Brand.';
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    }

    showToast.error(errorMessage);
    throw error;
  }
};

// Helper function for handling delete success
function handleDeleteSuccess(response: any): boolean {
  // Check for various success response formats
  if (response.data?.status === "Request successful!" ||
    response.data?.status === "Deleted!" ||
    response.status === 200 ||
    response.status === 204) {
    return true;
  }

  throw new Error('Invalid response format from API');
}

/**
 * Delete a Brand document
 * @param BrandIdentifier Brand ID or mobile number
 * @param documentType Document type to delete (shopFront, shopInner, evCer)
 * @param token Authentication token
 * @param isUsingMobile Flag to indicate if using mobile number instead of ID
 * @returns Promise indicating success
 */
export const deleteBrandDocument = async (
  BrandIdentifier: string,
  documentType: string,
  token: string | null,
  isUsingMobile: boolean = false
): Promise<boolean> => {
  try {
    console.log(`[API] Deleting document ${documentType} for Brand ${isUsingMobile ? 'mobile' : 'ID'}: ${BrandIdentifier}`);

    if (!token) {
      console.error('[API] Missing authentication token for document deletion');
      showToast.error('Authentication required to delete document');
      return false;
    }

    if (!BrandIdentifier || !documentType) {
      console.error('[API] Missing required parameters for document deletion');
      showToast.error('Missing required information for document deletion');
      return false;
    }

    try {
      let endpoint = '';

      if (isUsingMobile) {
        // API path for deletion by mobile number
        endpoint = `/users/Brand/docs/${documentType}/${BrandIdentifier}`;
      } else {
        // API path for deletion by Brand ID
        endpoint = `/users/Brand/docs/${documentType}/${BrandIdentifier}`;
      }

      console.log(`[API] Using endpoint: ${endpoint}`);

      const response = await axios.delete(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': token
        }
      });

      console.log('[API] Delete document response:', response.data);

      if (response.data?.status === "Request successful!") {
        // After successfully deleting the document, check if all documents are now deleted
        // If all are deleted, then update the Brand's KYC docs flag to false
        try {
          // Check if any other documents still exist
          const docTypes = ['shopFront', 'shopInner', 'evCer'];
          const otherTypes = docTypes.filter(type => type !== documentType);

          let allDocsDeleted = true;

          // Try to fetch each of the other document types
          for (const type of otherTypes) {
            try {
              const checkEndpoint = `/users/Brand/docs/${type}/${BrandIdentifier}`;
              const checkResponse = await axios.get(checkEndpoint, {
                headers: {
                  'Content-Type': 'application/json',
                  'token': token
                }
              });

              // If we successfully get a URL, at least one document still exists
              if (checkResponse.data?.status === "Request successful!" &&
                checkResponse.data.result?.url) {
                console.log(`[API] Document still exists: ${type}`);
                allDocsDeleted = false;
                break;
              }
            } catch (checkError: any) {
              // If the error is a 404 Not Found, it means the document doesn't exist
              if (checkError.response?.status === 404) {
                console.log(`[API] Document doesn't exist: ${type}`);
              } else {
                // For other errors, we assume the document might still exist
                console.error(`[API] Error checking document ${type}:`, checkError);
                allDocsDeleted = false;
                break;
              }
            }
          }

          // Only update the kyc.docs flag if all documents are deleted
          if (allDocsDeleted) {
            console.log('[API] All documents have been deleted, updating kyc.docs flag to false');

            // Determine which endpoint to use for the update
            const updateEndpoint = isUsingMobile
              ? `/users/Brand?mobileNo=${BrandIdentifier}`
              : `/users/Brand?id=${BrandIdentifier}`;

            // The update payload needs to set kyc.docs to false
            const updatePayload = {
              kyc: {
                docs: false
              }
            };

            console.log(`[API] Updating Brand KYC docs flag at endpoint: ${updateEndpoint}`);

            // Call the update endpoint
            const updateResponse = await axios.put(updateEndpoint, updatePayload, {
              headers: {
                'Content-Type': 'application/json',
                'token': token
              }
            });

            console.log('[API] Brand KYC flag update response:', updateResponse.data);
          } else {
            console.log('[API] Some documents still exist, keeping kyc.docs flag as true');
          }
        } catch (updateError) {
          console.error('[API] Error checking or updating Brand KYC docs flag:', updateError);
          // We don't fail the overall operation if just the flag update fails
        }

        return true;
      } else {
        console.warn('[API] Delete document returned success=false:', response.data);
        return false;
      }
    } catch (error: any) {
      console.error('[API] Error deleting document:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || 'Failed to delete document';
      showToast.error(errorMessage);
      return false;
    }
  } catch (error: any) {
    console.error('[API] Error in deleteBrandDocument function:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete document';
    showToast.error(errorMessage);

    return false;
  }
};

/**
 * Fetch a Brand document URL by mobile number or ID
 * @param BrandIdentifier Brand ID or mobile number
 * @param documentType Document type to fetch (shopFront, shopInner, evCer)
 * @param token Authentication token
 * @param isUsingMobile Flag to indicate if using mobile number instead of ID
 * @returns Promise with document URL
 */
export const fetchBrandDocument = async (
  BrandIdentifier: string,
  documentType: string,
  token: string | null,
  isUsingMobile: boolean = false
): Promise<string | null> => {
  try {
    console.log(`[API DEBUG] Fetching document ${documentType} for Brand ${isUsingMobile ? 'mobile' : 'ID'}: ${BrandIdentifier}`);
    console.log(`[API DEBUG] Token available: ${!!token}`);

    if (!token) {
      console.error('[API DEBUG] Missing authentication token for document fetch');
      showToast.error('Authentication required to view document');
      return null;
    }

    if (!BrandIdentifier || !documentType) {
      console.error('[API DEBUG] Missing required parameters for document fetch');
      showToast.error('Missing required information for document fetch');
      return null;
    }

    let endpoint = '';

    if (isUsingMobile) {
      // API path for fetching by mobile number
      endpoint = `/users/Brand/docs/${documentType}/${BrandIdentifier}`;
    } else {
      // API path for fetching by Brand ID
      endpoint = `/users/Brand/docs/${documentType}/${BrandIdentifier}`;
    }

    console.log(`[API DEBUG] Using endpoint: ${endpoint}`);

    try {
      console.log(`[API DEBUG] Sending GET request to: ${endpoint}`);
      const response = await axios.get(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': token
        }
      });

      console.log('[API DEBUG] Fetch document response status:', response.status);
      console.log('[API DEBUG] Fetch document response data:', response.data);

      if (response.data?.status === "Request successful!" && response.data.result?.url) {
        console.log(`[API DEBUG] Successfully got document URL: ${response.data.result.url.substring(0, 60)}...`);
        return response.data.result.url;
      } else {
        console.warn('[API DEBUG] Fetch document returned without URL:', response.data);
        return null;
      }
    } catch (error: any) {
      console.error('[API DEBUG] Error in axios request:', error.message);
      if (error.response) {
        console.error('[API DEBUG] Error response status:', error.response.status);
        console.error('[API DEBUG] Error response data:', error.response.data);
      }
      const errorMessage = error.response?.data?.message || 'Failed to fetch document';
      showToast.error(errorMessage);
      return null;
    }
  } catch (error: any) {
    console.error('[API DEBUG] Error fetching document:', error);
    const errorMessage = error.response?.data?.message || 'Failed to fetch document';
    showToast.error(errorMessage);
    return null;
  }
}; 